import { createFileRoute, Link, Outlet, useRouterState } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { ArrowLeft, Loader2, Users, Activity, ChevronRight } from "lucide-react";
import { crmListSalesPeople } from "@/lib/crm.functions";
import { TeamChat } from "@/components/crm/TeamChat";

export const Route = createFileRoute("/crm/sales")({
  component: SalesLayout,
});

function SalesLayout() {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  // If on a sub-route (e.g. /crm/sales/$userId), just render outlet
  if (pathname !== "/crm/sales") return <Outlet />;
  return <SalesPeopleList />;
}

function SalesPeopleList() {
  const list = useServerFn(crmListSalesPeople);
  const q = useQuery({ queryKey: ["crm", "sales-people"], queryFn: () => list() });

  return (
    <div className="h-full overflow-y-auto bg-background pb-12">
      <header className="sticky top-0 z-20 rounded-2xl border border-border bg-background/95 backdrop-blur">
        <div className="mx-auto max-w-7xl flex items-center gap-2 px-3 sm:px-4 lg:px-6 py-3">
          <Link to="/crm" className="rounded-md p-2 hover:bg-muted" aria-label="Back">
            <ArrowLeft className="h-4 w-4" />
          </Link>
          <h1 className="text-sm sm:text-base font-bold">Sales People</h1>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-3 sm:px-4 lg:px-6 py-4 sm:py-6 grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-4 lg:gap-6 items-start">
        <div className="min-w-0">
        {q.isLoading ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading sales people…
          </div>
        ) : q.error ? (
          <p className="text-sm text-destructive">{(q.error as Error).message}</p>
        ) : (q.data?.people ?? []).length === 0 ? (
          <p className="text-sm text-muted-foreground">No sales people yet.</p>
        ) : (
          <ul
            aria-label="Sales people"
            className="grid list-none grid-cols-1 gap-3 sm:grid-cols-2 sm:gap-4 lg:grid-cols-3 xl:grid-cols-4"
          >
            {q.data!.people.map((p: any) => {
              const name = p.display_name || p.email || p.user_id.slice(0, 8);
              const initial = (name || "?").charAt(0).toUpperCase();
              const palette = ["#0d6efd", "#10b981", "#a855f7", "#f59e0b", "#ef4444", "#06b6d4"];
              const tint = palette[
                Array.from((p.user_id || name) as string).reduce(
                  (acc: number, ch: string) => acc + ch.charCodeAt(0),
                  0,
                ) % palette.length
              ];
              return (
                <li key={p.user_id}>
                  <Link
                    to="/crm/sales/$userId"
                    params={{ userId: p.user_id }}
                    aria-label={`Open ${name}`}
                    className="group relative flex h-full flex-col overflow-hidden rounded-2xl border border-[#e7eaf0] bg-white shadow-[0_2px_10px_-2px_rgba(16,24,40,0.05)] transition-[transform,box-shadow,border-color] duration-200 ease-out will-change-transform hover:-translate-y-0.5 hover:border-[#0d6efd]/40 hover:shadow-[0_14px_32px_-14px_rgba(13,110,253,0.28)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0d6efd] focus-visible:ring-offset-2 motion-reduce:transform-none motion-reduce:transition-none"
                  >
                    <span
                      aria-hidden
                      className="pointer-events-none absolute inset-x-0 top-0 h-[3px]"
                      style={{ background: `linear-gradient(90deg, ${tint}, ${tint}66)` }}
                    />
                    <div className="flex items-center gap-3 p-3 pb-2.5 sm:p-4 sm:pb-3">
                      <div
                        aria-hidden
                        className="grid h-10 w-10 shrink-0 place-items-center rounded-xl text-sm font-bold text-white ring-4 sm:h-11 sm:w-11"
                        style={{
                          background: `linear-gradient(135deg, ${tint}, ${tint}cc)`,
                          boxShadow: `0 0 0 4px ${tint}1a`,
                        }}
                      >
                        {initial}
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center justify-between gap-2">
                          <p className="truncate text-[13px] font-bold leading-tight text-[#1a2540]">{name}</p>
                          <ChevronRight aria-hidden className="h-4 w-4 shrink-0 text-[#6c757d] transition-transform duration-200 group-hover:translate-x-0.5 group-hover:text-[#0d6efd] motion-reduce:transform-none" />
                        </div>
                        <p className="mt-0.5 truncate text-[11px] font-medium text-[#6c757d]">
                          {p.email || "—"}
                        </p>
                      </div>
                    </div>

                    <div className="mt-auto grid grid-cols-3 gap-px border-y border-[#e7eaf0] bg-[#e7eaf0]">
                      <Stat label="Clients" value={p.clients} icon={Users} tint={tint} />
                      <Stat label="Active" value={p.active} tint="#10b981" />
                      <Stat label="Activities" value={p.activities} icon={Activity} tint="#a855f7" />
                    </div>

                    <div className="flex items-center justify-between gap-2 px-3 py-2.5 sm:px-4">
                      <span className="text-[9px] font-bold uppercase tracking-wider text-[#6c757d]">
                        Last Activity
                      </span>
                      <span className="text-[11px] font-semibold tabular-nums text-[#1a2540]">
                        {p.last_active ? new Date(p.last_active).toLocaleDateString() : "—"}
                      </span>
                    </div>
                  </Link>
                </li>
              );
            })}
          </ul>


        )}
        </div>
        <aside className="lg:sticky lg:top-[68px]">
          <TeamChat />
        </aside>
      </main>
    </div>
  );
}

function Stat({
  label,
  value,
  icon: Icon,
  tint,
}: {
  label: string;
  value: number;
  icon?: any;
  tint: string;
}) {
  return (
    <div className="flex flex-col items-center justify-center gap-0.5 bg-[#f8f9fb] px-2 py-2.5">
      <div className="flex items-center gap-1 text-[9px] font-bold uppercase tracking-wider text-[#6c757d]">
        {Icon && <Icon className="h-3 w-3" style={{ color: tint }} />} {label}
      </div>
      <div className="text-base font-extrabold tabular-nums text-[#1a2540]">{value}</div>
    </div>
  );
}
